import axios from 'axios';

// Determine the correct backend URL at runtime
// CRITICAL: If running on production domain but REACT_APP_BACKEND_URL points elsewhere,
// force-correct to current origin. This prevents ALL stale-URL issues.
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined') return envUrl;
  
  const currentHost = window.location.hostname;
  // On production domains: always use current origin (prevents stale preview URLs)
  if (currentHost === 'app.salesqualifyd.com' || 
      currentHost.endsWith('.emergent.host') ||
      currentHost === 'salesqualifyd.com') {
    return window.location.origin;
  }
  // On preview/localhost: use env var as-is
  return envUrl || window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export const API_URL = `${BACKEND_URL}/api`;
export { BACKEND_URL };

const api = axios.create({
  baseURL: BACKEND_URL,
});

// Request interceptor: attach token + force-correct URL on every request
api.interceptors.request.use((config) => {
  // Only auto-attach main app token if the request doesn't already have an Authorization header
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Double-safety: if on app.salesqualifyd.com, rewrite preview URLs to production
  if (config.url && typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const currentOrigin = window.location.origin;
    // Only rewrite if we're on a known production domain and URL points elsewhere
    if (currentHost === 'app.salesqualifyd.com' || currentHost.endsWith('.emergent.host')) {
      const stalePatterns = ['preview.emergentagent.com', 'salescrm-portal'];
      for (const pattern of stalePatterns) {
        if (config.url && config.url.includes(pattern)) {
          config.url = config.url.replace(/https?:\/\/[^/]+/, currentOrigin);
        }
        if (config.baseURL && config.baseURL.includes(pattern)) {
          config.baseURL = currentOrigin;
        }
      }
    }
  }
  return config;
});

// Response interceptor: only redirect to login on EXPLICIT 401, never on network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on explicit 401 from server — NOT on network/CORS errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const publicPaths = ['/login', '/pricing', '/payment', '/setup-profile', '/privacy-policy', '/terms-of-use', '/refund-policy', '/learn', '/set-password', '/trial-expired', '/auth/', '/f/', '/courses', '/s/'];
      if (!publicPaths.some(p => window.location.pathname.startsWith(p))) {
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 402 && error.response?.data?.detail === 'TRIAL_EXPIRED') {
      if (!['/trial-expired', '/pricing', '/payment'].includes(window.location.pathname)) {
        window.location.href = '/trial-expired';
      }
    }
    if (error.response?.status === 402 && error.response?.data?.detail === 'SUBSCRIPTION_LOCKED') {
      const safePages = ['/dashboard', '/pricing', '/payment', '/settings', '/login', '/trial-expired'];
      if (!safePages.some(p => window.location.pathname.startsWith(p))) {
        window.location.href = '/dashboard?locked=true';
      }
    }
    return Promise.reject(error);
  }
);

// GET de-duplication + 30s memory for the "app shell" calls every page makes.
// Any write (POST/PUT/PATCH/DELETE) clears it so your own changes always show immediately.
const SHELL_TTL_MS = 30000;
const SHELL_PATHS = ['/api/auth/me', '/api/workspaces', '/api/admin/my-permissions', '/api/subscriptions/status',
  '/api/gtm/projects?summary=1', '/api/mpo/usage', '/api/pipeline/pipelines', '/api/pipeline/members'];
const inflight = new Map();
const shellCache = new Map();
const keyFor = (url, config) => `${localStorage.getItem('token') || ''}|${url}|${JSON.stringify(config?.params || {})}|${config?.headers?.Authorization || ''}`;
const isShell = (url) => SHELL_PATHS.some((p) => url.endsWith(p));
const rawGet = api.get.bind(api);
api.get = (url, config) => {
  const key = keyFor(url, config);
  const hit = shellCache.get(key);
  if (hit && Date.now() - hit.at < SHELL_TTL_MS) return Promise.resolve(hit.res);
  if (inflight.has(key)) return inflight.get(key);
  const p = rawGet(url, config)
    .then((res) => { if (isShell(url)) shellCache.set(key, { at: Date.now(), res }); return res; })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};
export const clearApiCache = () => shellCache.clear();
['post', 'put', 'patch', 'delete'].forEach((m) => {
  const raw = api[m].bind(api);
  api[m] = (...args) => { shellCache.clear(); return raw(...args); };
});

export default api;
